import React, { useEffect, useState } from 'react';
import { useBunkerContext } from 'src/context/BunkerContext';
import BunkerRegistrationListItem from './BunkerRegistrationListItem';
import { BunkeringRegTableStyle, BunkeringRegTableWrapper, TableColumnHeaderStyle } from '../Bunkering.styles';
import { addDateHours, isDateSameOrAfter, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import DebunkeringListItem from './debunkering/DebunkeringListItem';
import { UUID } from 'crypto';
import { BunkeringEvent, BunkeringStatusEnum } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { getOneYearBefore } from '../BunkeringUtils';
import { useAolStore } from 'src/context/AolContext';
import DeletedBunkeringListItem from './DeletedBunkeringListItem';

interface BunkerRegistrationListProps {
    selectedBunkeringEventId: string;
    selectedDeletedBunkering: string;
    setSelectedBunkeringEvent: (id: string) => void;
    setSelectedDeletedBunkering: (id: string) => void;
}

const BunkerRegistrationList: React.FC<BunkerRegistrationListProps> = ({
    selectedBunkeringEventId,
    selectedDeletedBunkering,
    setSelectedBunkeringEvent,
    setSelectedDeletedBunkering,
}: BunkerRegistrationListProps) => {
    const { bunkeringEvents, bunkerings, selectedBunkeringDate, deletedBunkerings } = useBunkerContext();
    const { aolFilter } = useAolStore();
    const [relevantBunkeringEvents, setRelevantBunkeringEvents] = useState<BunkeringEvent[]>();

    useEffect(() => {
        setRelevantBunkeringEvents(
            bunkeringEvents?.filter(
                (bunkeringEvent) =>
                    isDateSameOrAfter(bunkeringEvent.bunkeringDateTimeLocal, getOneYearBefore(aolFilter.fromDate)) ||
                    bunkerings.find((b) => b.eventId === (bunkeringEvent.eventID as UUID))?.currentStatus?.status === BunkeringStatusEnum.Approved ||
                    bunkerings.find((b) => b.eventId === (bunkeringEvent.eventID as UUID))?.currentStatus?.status === BunkeringStatusEnum.InUse
            )
        );
    }, [bunkeringEvents, bunkerings, aolFilter]);

    const handleBunkeringEventSelection = (id: string) => {
        if (selectedBunkeringEventId === id) {
            setSelectedBunkeringEvent(null);
        } else {
            setSelectedBunkeringEvent(id);
            setSelectedDeletedBunkering(null);
        }
    };

    const handleDeletedBunkeringSelection = (id: string) => {
        if (selectedDeletedBunkering === id) {
            setSelectedDeletedBunkering(null);
        } else {
            setSelectedDeletedBunkering(id);
            setSelectedBunkeringEvent(null);
        }
    };

    return (
        <BunkeringRegTableWrapper>
            <BunkeringRegTableStyle>
                <thead>
                    <tr>
                        <TableColumnHeaderStyle width={'35%'}>BunkeringRef</TableColumnHeaderStyle>
                        <TableColumnHeaderStyle width={'10%'}>Status</TableColumnHeaderStyle>
                        <TableColumnHeaderStyle width={'30%'}>Date/Time</TableColumnHeaderStyle>
                        <TableColumnHeaderStyle width={'25%'}>Voyage</TableColumnHeaderStyle>
                    </tr>
                </thead>
                <tbody>
                    {bunkeringEvents
                        ?.filter(
                            (bunkeringEvent) =>
                                bunkeringEvent.debunkering && !bunkerings.some((b) => b.debunkeringEventIds.includes(bunkeringEvent.eventID as UUID))
                        )
                        .map((bunkeringEvent, index) => (
                            <DebunkeringListItem
                                key={index}
                                debunkeringEvent={bunkeringEvent}
                                selected={selectedBunkeringEventId === bunkeringEvent.id}
                                onSelectBunkeringEvent={handleBunkeringEventSelection}
                            />
                        ))}
                    {relevantBunkeringEvents
                        ?.filter(
                            (bunkeringEvent) =>
                                !bunkeringEvent?.debunkering &&
                                isDateSameOrBefore(bunkeringEvent.bunkeringDateTimeLocal, addDateHours(selectedBunkeringDate, 12))
                        )
                        .map((bunkeringEvent, index) => (
                            <BunkerRegistrationListItem
                                key={index}
                                bunkeringEvent={bunkeringEvent}
                                selected={selectedBunkeringEventId === bunkeringEvent.id}
                                onSelectBunkeringEvent={handleBunkeringEventSelection}
                            />
                        ))}
                    {deletedBunkerings.map((bunkering, index) => (
                        <DeletedBunkeringListItem
                            key={index}
                            bunkering={bunkering}
                            selected={selectedDeletedBunkering === bunkering.bunkeringGuid}
                            onSelectDeletedBunkering={handleDeletedBunkeringSelection}
                        />
                    ))}
                </tbody>
            </BunkeringRegTableStyle>
        </BunkeringRegTableWrapper>
    );
};

export default BunkerRegistrationList;
